import { useMemo, useRef, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { DateView, getLastSevenDaysDates } from "./dateView";
import { StatCard } from "./statCard";
import { useDepotAnalyticsByIdQuery, useDepotByIdQuery } from "~/shared/api/govDash/govDash.api";
import {
  formatTimestampToDate,
  toLocalWallClockTimestamp,
} from "~/shared/utils/filterDateFormatter";
import { ConnectProto } from "@racoon/api";

type DepotDetailsSection = "general" | "summary" | "autopark";

export const DepotDetails = ({ selectedRecordId }: { selectedRecordId: string | undefined }) => {
  const { data: depotData } = useDepotByIdQuery({
    id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
  });

  const { weekStart: initialWeekStart, weekEnd: initialWeekEnd } = getLastSevenDaysDates();
  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [weekEnd, setWeekEnd] = useState(initialWeekEnd);
  const activityListRef = useRef<HTMLDivElement | null>(null);
  const { data: depotAnalyticsData } = useDepotAnalyticsByIdQuery(
    {
      depotId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );

  const t = useT();
  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
        { value: "autopark" as const, label: t(Namespace.Govdash, "autopark") },
      ] as const,
    [t],
  );

  const [activeSection, setActiveSection] = useState<DepotDetailsSection>("general");
  const sortedTruckActivities = useMemo(() => {
    if (activeSection !== "autopark") return [];
    return [...(depotAnalyticsData?.truckActities ?? [])].sort((a, b) => {
      return Number(b?.lastActive?.seconds ?? 0) - Number(a?.lastActive?.seconds ?? 0);
    });
  }, [depotAnalyticsData?.truckActities, activeSection]);
  const activityVirtualizer = useVirtualizer({
    count: sortedTruckActivities.length,
    getScrollElement: () => activityListRef.current,
    estimateSize: () => 78,
    overscan: 6,
  });

  const generalRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "subtitle.serviceProvider"),
        value: depotData?.ownerOrganization?.name || "-",
      },
      {
        label: t(Namespace.Govdash, "autopark"),
        value: `${depotData?.numberOfTrucks ?? 0} ${t(Namespace.Govdash, "vehicles")}`,
      },
      {
        label: t(Namespace.Govdash, "geolocation"),
        value: (
          <a
            className="block max-w-xs min-w-20 truncate"
            href={`https://2gis.kz/geo/${depotData?.depot?.lon},${depotData?.depot?.lat}`}
            target="_blank"
            rel="noreferrer"
          >
            https://2gis.kz/geo/{depotData?.depot?.lon},{depotData?.depot?.lat}
          </a>
        ),
      },
      {
        label: t(Namespace.Govdash, "depotAddress"),
        value: depotData?.depot?.address || "-",
      },
      // { label: t(Namespace.Govdash, "servingArea"), value: "Bostandyk district" },
    ],
    [t, depotData],
  );

  return (
    <CardContentWrapper
      hasDivider={activeSection !== "general"}
      className="flex min-h-0 flex-col overflow-hidden pb-2"
    >
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      {activeSection === "general" && (
        <div className="overflow-y-auto min-h-0 flex-1">
          <div className="p-4 space-y-4">
            <CardContentWrapper>
              {generalRows.map((row) => (
                <InfoRow key={row.label} label={row.label} value={row.value} />
              ))}
            </CardContentWrapper>
          </div>
        </div>
      )}
      {activeSection === "summary" && (
        <div className="overflow-y-auto min-h-0 flex-1">
          <DateView
            weekStart={weekStart}
            weekEnd={weekEnd}
            onDateChange={(from, to) => {
              if (from) setWeekStart(from);
              if (to) setWeekEnd(to);
            }}
          />
          <div className="grid grid-cols-2 gap-2 p-4">
            {/* <StatCard title={t(Namespace.Govdash, "wentOnLine")} value={98} /> */}
            {/* <StatCard title={t(Namespace.Govdash, "wasteVolume")} value={89.1} /> */}
            {/* <StatCard title={t(Namespace.Govdash, "mileage")} value={450} /> */}
            {/* <StatCard title={t(Namespace.Govdash, "anomalies")} value={5} /> */}
            <StatCard
              title={t(Namespace.Govdash, "analytics.totalTrucks")}
              value={depotAnalyticsData?.totalTrucks?.toString() ?? "0"}
            />
            <StatCard
              title={t(Namespace.Govdash, "analytics.trucksActive")}
              value={depotAnalyticsData?.trucksActive?.toString() ?? "0"}
            />
          </div>
        </div>
      )}
      {activeSection === "autopark" && (
        <>
          <div className="p-4 text-xs flex gap-4 w-full flex-1 font-bold text-text-secondary uppercase">
            <p>№</p>
            <div className="flex justify-between w-full">
              <p>{t(Namespace.Govdash, "plateNumber")}</p>
              <p>{t(Namespace.Govdash, "goingOnLine")}</p>
            </div>
          </div>
          {sortedTruckActivities.length > 0 ? (
            <div ref={activityListRef} className="overflow-y-auto">
              <div
                role="list"
                className="relative w-full"
                style={{ height: `${activityVirtualizer.getTotalSize()}px` }}
              >
                {activityVirtualizer.getVirtualItems().map((virtualRow) => {
                  const activity = sortedTruckActivities[virtualRow.index];
                  return (
                    <div
                      role="listitem"
                      aria-setsize={sortedTruckActivities.length}
                      aria-posinset={virtualRow.index + 1}
                      key={activity.truck?.id.toString() || `activity-${virtualRow.index}`}
                      data-index={virtualRow.index}
                      ref={activityVirtualizer.measureElement}
                      className="absolute left-0 top-0 w-full"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <div className="flex w-full gap-4 border-b border-slate-200 bg-white p-4 text-left items-center">
                        <span className="text-xs/4 font-bold uppercase leading-none tracking-tight text-slate-900">
                          {virtualRow.index + 1 || "-"}
                        </span>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${activity?.truck?.status === ConnectProto.TruckStatus.ACTIVE ? "bg-green-500" : "bg-red-500"}`}
                            />
                            <span className="inline-flex rounded border border-slate-900 px-1 py-0.5 text-xs font-medium leading-4 tracking-tight text-slate-900">
                              {activity?.truck?.plateNumber || "-"}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-normal leading-4 tracking-tight text-text-secondary">
                            <span>{activity?.truck?.model || "-"}</span>
                            <span>{formatTimestampToDate(activity.lastActive)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-sm text-text-secondary">{t(Namespace.Trucks, "tableNoData")}</p>
            </div>
          )}
        </>
      )}
    </CardContentWrapper>
  );
};
